import { BusinessAccess } from '../hooks/useBusinessAccessInvitations';
import { AccessLabelMap, getAccessTypeLabel } from '../hooks/useAccessTypeOptions';
import { passStatusLabel } from '../pages/invitation/passStatusLabel';

export interface AccessTypeGroup {
  type: string;
  typeId: number;
  count: number;
  percentage: number;
}

export interface StatusGroup {
  status: string;
  statusId: number;
  count: number;
  percentage: number;
  color: string;
}

export interface TimeSeriesDataPoint {
  date: string;
  timestamp: number;
  total: number;
  pending: number;
  approved: number;
  in: number;
  out: number;
  cancelled: number;
  rejected: number;
}

export interface HeatmapDataPoint {
  day: string;
  hour: number;
  count: number;
}

export interface TopGuest {
  name: string;
  visits: number;
  lastVisit: string;
  mostCommonType: string;
}

export interface ChannelDistribution {
  channel: string;
  channelId: number;
  count: number;
  percentage: number;
}

// SAM-581: access-type and access-status labels used to be hardcoded here
// and disagreed with the labels hardcoded elsewhere (dashboard filters,
// invitations table) for the same id. Callers now pass in the live
// id->label map from useAccessTypeLabelMap()/useAccessStatusLabelMap()
// (src/hooks/useAccessTypeOptions.ts), which resolves ids against the
// real business_access_type / business_access_status backend records.
// Colors are purely presentational (not a label), so they stay hardcoded.
export const STATUS_COLORS: Record<number, string> = {
  1: '#FFA500', // Orange - Pending
  2: '#4CAF50', // Green - Approved
  3: '#2196F3', // Blue - In
  4: '#F44336', // Red - Rejected
  5: '#9E9E9E', // Gray - Cancelled
  6: '#607D8B', // Blue Gray - Out
};

/**
 * Agrupa datos por tipo de acceso
 */
export const groupByAccessType = (data: BusinessAccess[], labelMap?: AccessLabelMap): AccessTypeGroup[] => {
  const total = data.length;
  // SAM-1320 (U39): a row names its own type (`type_name`, resolved by the backend from the right table: a pass of
  // the business and an invitation do not share ids), so rows group by that name; the id lookup is the fallback.
  const groups = new Map<string, { typeId: number; label: string; count: number }>();

  data.forEach(item => {
    const typeId = item.business_access_type_id;
    const label = (typeof item.type_name === 'string' && item.type_name.trim()) || (typeId == null ? 'Unknown' : labelMap?.[Number(typeId)] || `Type ${typeId}`);
    const key = label;
    const g = groups.get(key);
    if (g) g.count += 1;
    else groups.set(key, { typeId, label, count: 1 });
  });

  return Array.from(groups.values())
    .map(({ typeId, label, count }) => ({
      type: label,
      typeId,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Agrupa datos por estado
 */
export const groupByStatus = (data: BusinessAccess[], labelMap?: AccessLabelMap): StatusGroup[] => {
  const total = data.length;
  // SAM-1320 (U39): same as the types, by the row's own `status_name`.
  const groups = new Map<string, { statusId: number; label: string; count: number }>();

  data.forEach(item => {
    const statusId = item.business_access_status_id;
    const label = passStatusLabel((typeof item.status_name === 'string' && item.status_name.trim()) || (statusId == null ? 'Unknown' : labelMap?.[Number(statusId)] || `Status ${statusId}`));
    const key = label.toLowerCase();
    const g = groups.get(key);
    if (g) g.count += 1;
    else groups.set(key, { statusId, label, count: 1 });
  });

  return Array.from(groups.values())
    .map(({ statusId, label, count }) => ({
      status: label,
      statusId,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
      color: STATUS_COLORS[statusId] || '#000000',
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Calcula la tasa de conversión (aprobados / total)
 */
export const calculateConversionRate = (data: BusinessAccess[]): number => {
  const total = data.length;
  if (total === 0) return 0;

  const approved = data.filter(item => 
    item.business_access_status_id === 2 || item.business_access_status_id === 3
  ).length;

  return (approved / total) * 100;
};

/**
 * Calcula el promedio de invitados por acceso
 */
export const calculateAverageGuests = (data: BusinessAccess[]): number => {
  if (data.length === 0) return 0;

  const totalGuests = data.reduce((sum, item) => {
    const businessGuests = item.business_guests?.length || 0;
    const children = item.children_count || 0;
    return sum + businessGuests + children + 1; // +1 for main guest
  }, 0);

  return totalGuests / data.length;
};

/**
 * Prepara datos para gráfico de series temporales
 */
export const getTimeSeriesData = (
  data: BusinessAccess[],
  granularity: 'hour' | 'day' | 'week' | 'month'
): TimeSeriesDataPoint[] => {
  const groups = new Map<string, BusinessAccess[]>();
  // Store the first valid Date for each key so we don't have to re-parse truncated key strings
  const groupDates = new Map<string, Date>();

  data.forEach(item => {
    const date = new Date(item.start_date);
    if (isNaN(date.getTime())) return; // skip invalid dates
    let key: string;

    switch (granularity) {
      case 'hour':
        key = `${date.toISOString().split('T')[0]}T${date.getHours().toString().padStart(2, '0')}`;
        break;
      case 'day':
        key = date.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
        if (!groupDates.has(key)) groupDates.set(key, weekStart);
        break;
      case 'month':
        key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        break;
    }

    if (!groupDates.has(key)) {
      groupDates.set(key, date);
    }
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(item);
  });

  return Array.from(groups.entries())
    .map(([key, items]) => {
      // Use the stored valid Date instead of re-parsing the key
      const displayDate = groupDates.get(key) || new Date();
      const timestamp = displayDate.getTime();
      return {
        date: formatDateForDisplay(displayDate, granularity),
        timestamp,
        total: items.length,
        pending: items.filter(i => i.business_access_status_id === 1).length,
        approved: items.filter(i => i.business_access_status_id === 2).length,
        in: items.filter(i => i.business_access_status_id === 3).length,
        out: items.filter(i => i.business_access_status_id === 6).length,
        cancelled: items.filter(i => i.business_access_status_id === 5).length,
        rejected: items.filter(i => i.business_access_status_id === 4).length,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Prepara datos para mapa de calor día/hora
 */
export const getHeatmapData = (data: BusinessAccess[]): HeatmapDataPoint[] => {
  const heatmap: HeatmapDataPoint[] = [];
  const groups = new Map<string, number>();

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  data.forEach(item => {
    const date = new Date(item.start_date);
    const day = days[date.getDay()];
    const hour = date.getHours();
    const key = `${day}-${hour}`;

    groups.set(key, (groups.get(key) || 0) + 1);
  });

  // Generar todos los puntos (7 días x 24 horas)
  days.forEach(day => {
    for (let hour = 0; hour < 24; hour++) {
      const key = `${day}-${hour}`;
      heatmap.push({
        day,
        hour,
        count: groups.get(key) || 0,
      });
    }
  });

  return heatmap;
};

/**
 * Obtiene los invitados más frecuentes
 */
export const getTopGuests = (data: BusinessAccess[], limit: number = 10, labelMap?: AccessLabelMap): TopGuest[] => {
  const guestMap = new Map<string, { visits: number; lastVisit: Date; types: number[] }>();

  data.forEach(item => {
    if (item.main_guest) {
      const name = `${item.main_guest.name} ${item.main_guest.last_name}`.trim();
      const existing = guestMap.get(name);
      const visitDate = new Date(item.start_date);

      if (existing) {
        existing.visits++;
        if (visitDate > existing.lastVisit) {
          existing.lastVisit = visitDate;
        }
        existing.types.push(item.business_access_type_id);
      } else {
        guestMap.set(name, {
          visits: 1,
          lastVisit: visitDate,
          types: [item.business_access_type_id],
        });
      }
    }
  });

  return Array.from(guestMap.entries())
    .map(([name, data]) => {
      // Encontrar el tipo más común
      const typeCounts = new Map<number, number>();
      data.types.forEach(type => {
        typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
      });
      const mostCommonTypeId = Array.from(typeCounts.entries())
        .sort((a, b) => b[1] - a[1])[0][0];

      return {
        name,
        visits: data.visits,
        lastVisit: data.lastVisit.toISOString(),
        mostCommonType: getAccessTypeLabel(labelMap, mostCommonTypeId) || `Type ${mostCommonTypeId}`,
      };
    })
    .sort((a, b) => b.visits - a.visits)
    .slice(0, limit);
};

/**
 * Agrupa datos por canal
 */
export const groupByChannel = (data: BusinessAccess[]): ChannelDistribution[] => {
  const total = data.length;
  const groups = new Map<number, number>();

  data.forEach(item => {
    const channelId = item.id_channel;
    groups.set(channelId, (groups.get(channelId) || 0) + 1);
  });

  return Array.from(groups.entries())
    .map(([channelId, count]) => ({
      channel: `Channel ${channelId}`,
      channelId,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Formatea fecha para visualización según granularidad
 */
const formatDateForDisplay = (date: Date, granularity: 'hour' | 'day' | 'week' | 'month'): string => {
  // Validar que la fecha sea válida
  if (!date || isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  try {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    // SAM-1312 (U37): the month is written out, never numeric (dd/mm vs mm/dd is read both ways).
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayLabel = `${monthNames[month - 1]} ${day}, ${year}`;

    switch (granularity) {
      case 'hour':
        return `${dayLabel} ${date.getHours().toString().padStart(2, '0')}:00`;
      case 'day':
      case 'week':
        return dayLabel;
      case 'month':
        return `${monthNames[month - 1]} ${year}`;
      default:
        return dayLabel;
    }
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return 'Invalid Date';
  }
};

/**
 * Calcula el cambio porcentual entre dos valores
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};
